import React, { forwardRef, useState, useEffect, useMemo, useRef } from 'react';
import { tr } from '../../../i18n/i18n';
import { ModalHandle, PrimaryModal, ConfirmationModal } from '../';
import { AppButton, AppInput, AppCalenderInput, AppTextarea } from '../../atoms';
import { IStudentFee } from '../../../interface/studentBillingModel';
import {
  INVOICE_STATUS_API,
  PAYMENT_STATUS,
  INVOICE_UI_VARIANT,
  normalizeInvoiceStatus,
} from '../../../constant/billing.constant';
import styles from './CollectPaymentModal.module.css';
import { formatDate } from '../../../utility/utility';

export type CollectPaymentModalMode = 'payment' | 'refund';

interface CollectPaymentModalProps {
  invoice: IStudentFee | null;
  mode?: CollectPaymentModalMode;
  onConfirm?: (data: ManualPaymentData) => void;
  onRefundConfirm?: (assignInvoiceId: string, data: RefundPaymentData) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

export interface ManualPaymentData {
  amount_received: number;
  payment_date?: string;
  reference_note?: string;
}

export interface RefundPaymentData {
  refund_amount: number;
  refund_reason: string;
  refund_date?: string;
  reference_note?: string;
}

const CollectPaymentModal = forwardRef<ModalHandle, CollectPaymentModalProps>(
  ({ invoice, mode = 'payment', onConfirm, onRefundConfirm, onCancel, isLoading = false }, ref) => {
    const isRefundMode = mode === 'refund';

    // Form state
    const [amountReceived, setAmountReceived] = useState<string>('');
    const [paymentDate, setPaymentDate] = useState<Date | null>(new Date());
    const [referenceNote, setReferenceNote] = useState<string>('');
    const [errors, setErrors] = useState<{ [key: string]: string }>({});

    // Refund confirmation modal (refund mode only — payment mode submits directly)
    const refundConfirmModalRef = useRef<ModalHandle>(null);

    // Calculate derived values
    const balanceDue = useMemo(() => {
      if (!invoice) return 0;
      return parseFloat(invoice.pending_amount || '0');
    }, [invoice]);

    const totalAmount = useMemo(() => {
      if (!invoice) return 0;
      return parseFloat(invoice.total_amount || '0');
    }, [invoice]);

    const alreadyPaid = useMemo(() => {
      if (!invoice) return 0;
      return totalAmount - balanceDue;
    }, [invoice, totalAmount, balanceDue]);

    // Refundable amount = what has actually been paid
    const refundableAmount = useMemo(() => {
      return alreadyPaid;
    }, [alreadyPaid]);

    const currency = localStorage.getItem('currency') || 'LKR';

    // Determine UI variant based on invoice.payment_status field
    const uiVariant = useMemo((): INVOICE_UI_VARIANT => {
      if (!invoice) return INVOICE_UI_VARIANT.UNPAID;

      const status = normalizeInvoiceStatus(invoice.payment_status);
      const isOverdue = status === INVOICE_STATUS_API.OVERDUE;
      const isOverduePartial = status === INVOICE_STATUS_API.OVERDUE_PARTIAL;
      const isPartiallyPaid = status === INVOICE_STATUS_API.PARTIAL;

      if (isOverdue) return INVOICE_UI_VARIANT.OVERDUE;
      if (isOverduePartial) return INVOICE_UI_VARIANT.OVERDUE_PARTIAL;
      if (isPartiallyPaid) return INVOICE_UI_VARIANT.PARTIAL;
      return INVOICE_UI_VARIANT.UNPAID;
    }, [invoice]);

    // Calculate payment status based on amount entered (dynamic)
    const paymentStatus = useMemo((): PAYMENT_STATUS | null => {
      if (!amountReceived || !balanceDue) return null;
      const amount = parseFloat(amountReceived);
      if (isNaN(amount) || amount <= 0) return null;

      if (amount < balanceDue) return PAYMENT_STATUS.PARTIAL;
      if (amount === balanceDue) return PAYMENT_STATUS.EXACT;
      return PAYMENT_STATUS.OVERPAYMENT;
    }, [amountReceived, balanceDue]);

    // Calculate remaining balance after entered amount
    const remainingBalance = useMemo(() => {
      if (!amountReceived || !balanceDue) return balanceDue;
      const amount = parseFloat(amountReceived);
      if (isNaN(amount)) return balanceDue;
      return Math.max(balanceDue - amount, 0);
    }, [amountReceived, balanceDue]);

    // Calculate payment progress for partial payment
    const paymentProgress = useMemo(() => {
      if (!amountReceived || balanceDue === 0) return 0;
      const amount = parseFloat(amountReceived);
      if (isNaN(amount)) return 0;
      return Math.min((amount / balanceDue) * 100, 100);
    }, [amountReceived, balanceDue]);

    // Reset form when modal opens with new invoice
    useEffect(() => {
      if (invoice) {
        // In refund mode, auto-populate with full refundable amount
        setAmountReceived(isRefundMode ? refundableAmount.toString() : '');
        setPaymentDate(new Date());
        setReferenceNote('');
        setErrors({});
      }
    }, [invoice, isRefundMode, refundableAmount]);

    // Validate form
    const validateForm = (): boolean => {
      const newErrors: { [key: string]: string } = {};

      if (!amountReceived || amountReceived.trim() === '') {
        newErrors.amountReceived = isRefundMode
          ? tr('g.x_refund_amount_is_required', 'Refund amount is required')
          : tr('g.x_amount_received_is_required', 'Amount received is required');
      } else {
        const amount = parseFloat(amountReceived);
        if (isNaN(amount) || amount <= 0) {
          newErrors.amountReceived = tr(
            'fe_modals.please_enter_a_valid_amount',
            'Please enter a valid amount',
          );
        } else if (isRefundMode && amount !== refundableAmount) {
          // In full refund mode, amount must exactly match refundable amount
          newErrors.amountReceived = `Full refund required: ${currency} ${refundableAmount.toLocaleString()}`;
        } else if (!isRefundMode && amount > balanceDue) {
          // "Amount Received" is the amount applied to THIS invoice, so it can never exceed the
          // balance due. Overpayment is blocked outright (the backend rejects it with a 400 too).
          newErrors.amountReceived = tr(
            'fe_modals.amount_cannot_exceed_balance_due',
            'Cannot exceed the balance due ({{amount}})',
            { amount: `${currency} ${balanceDue.toLocaleString()}` },
          );
        }
      }

      if (!paymentDate) {
        newErrors.paymentDate = isRefundMode
          ? tr('g.x_refund_date_is_required', 'Refund date is required')
          : tr('g.x_payment_date_is_required', 'Payment date is required');
      } else if (isRefundMode && paymentDate > new Date()) {
        newErrors.paymentDate = tr(
          'fe_modals.refund_date_cannot_be_in_the_future',
          'Refund date cannot be in the future',
        );
      }

      setErrors(newErrors);
      return Object.keys(newErrors).length === 0;
    };

    // Handle record payment button click
    const handleRecordPayment = () => {
      if (!validateForm()) return;

      if (isRefundMode) {
        // For refund, show confirmation dialog
        refundConfirmModalRef.current?.openModal();
        return;
      }

      // Proceed with payment. validateForm has already capped the amount at the balance due.
      submitPayment();
    };

    // Submit payment data
    const submitPayment = () => {
      const paymentData: ManualPaymentData = {
        amount_received: parseFloat(amountReceived),
        payment_date: paymentDate?.toISOString(),
        reference_note: referenceNote.trim() || undefined,
      };

      onConfirm?.(paymentData);
    };

    // Submit refund data
    const submitRefund = () => {
      if (!invoice) return;
      const refundData: RefundPaymentData = {
        refund_amount: parseFloat(amountReceived),
        refund_reason:
          referenceNote.trim() ||
          tr('fe_modals.refund_processed_by_cashier', 'Refund processed by cashier'),
        ...(paymentDate && { refund_date: paymentDate.toISOString() }),
        ...(referenceNote.trim() && { reference_note: referenceNote.trim() }),
      };

      onRefundConfirm?.(invoice.assign_invoice_id, refundData);
    };

    // Handle refund confirmation
    const handleRefundConfirm = () => {
      refundConfirmModalRef.current?.closeModal();
      submitRefund();
    };

    const handleRefundCancel = () => {
      refundConfirmModalRef.current?.closeModal();
    };

    if (!invoice) return null;

    return (
      <>
        <PrimaryModal
          ref={ref}
          title={
            isRefundMode
              ? tr('invoice.refund_payment', 'Refund Payment')
              : tr('invoice.collect_payment', 'Collect Payment')
          }
          width="600px"
          showHeader={false}
          onClose={onCancel}
          content={
            <div className="flex flex-col gap-4">
              {/* Modal Header */}
              <div className="flex items-center gap-3 pb-4 border-b border-gray-200">
                <div
                  className={`rounded-full w-12 h-12 flex items-center justify-center ${
                    isRefundMode ? styles.headerIconRefund : 'bg-[#DCFCE7]'
                  }`}
                >
                  <i
                    className={`pi ${
                      isRefundMode
                        ? `pi-history text-2xl ${styles.headerIconRefundColor}`
                        : 'pi-receipt text-2xl text-[#00A63E]'
                    }`}
                  ></i>
                </div>
                <span className="text-[20px] font-semibold font-poppins">
                  {isRefundMode
                    ? tr('invoice.refund_payment', 'Refund Payment')
                    : tr('invoice.collect_payment', 'Collect Payment')}
                </span>
              </div>

              {/* Invoice Card */}
              <div
                className={`border-[1.6px] border-project-purple rounded-[10px] p-5 ${styles.invoiceCardBg}`}
              >
                {/* Invoice Header */}
                <div className="flex justify-between items-start mb-3">
                  <div>
                    {isRefundMode ? (
                      <>
                        <h3 className="text-[16px] font-semibold text-[#101828] font-poppins">
                          {invoice.display_name}
                        </h3>
                        <p className="text-[14px] text-[#4A5565] font-poppins">
                          {tr('invoice.admission_no', 'AD NO: {{no}}', {
                            no: invoice.admission_no,
                          })}
                        </p>
                      </>
                    ) : (
                      <>
                        <h3 className="text-[16px] font-semibold text-[#101828] font-poppins">
                          {invoice.template_name}
                        </h3>
                        <p className="text-[14px] text-[#4A5565] font-poppins">
                          {tr(
                            'invoice.invoice_id_created',
                            'Invoice ID: {{id}} • Created: {{date}}',
                            {
                              id: invoice.invoice_id,
                              date: new Date(invoice.created).toLocaleDateString(),
                            },
                          )}
                        </p>
                      </>
                    )}
                  </div>
                  {isRefundMode ? (
                    <span
                      className={`px-2 py-0.5 text-[12px] font-medium rounded-[22px] ${styles.refundBadge}`}
                    >
                      {tr('fe_modals.refund', 'Refund')}
                    </span>
                  ) : (
                    <>
                      {uiVariant === INVOICE_UI_VARIANT.UNPAID && (
                        <span className="px-2 py-0.5 text-[12px] font-medium text-[#364153] bg-[#F3F4F6] border border-[#D1D5DC] rounded-[22px]">
                          {tr('fe_billing.status_unpaid', 'UNPAID')}
                        </span>
                      )}
                      {uiVariant === INVOICE_UI_VARIANT.OVERDUE && (
                        <span className="px-2 py-0.5 text-[12px] font-medium text-[#C10007] bg-[#FEE2E2] border border-[#FCA5A5] rounded-[22px]">
                          {tr('status.overdue', 'Overdue')}
                        </span>
                      )}
                      {uiVariant === INVOICE_UI_VARIANT.OVERDUE_PARTIAL && (
                        <span className="px-2 py-0.5 text-[12px] font-medium text-[#C10007] bg-[#FEE2E2] border border-[#FCA5A5] rounded-[22px]">
                          {tr('status.overdue_partial', 'Overdue Partial')}
                        </span>
                      )}
                    </>
                  )}
                </div>

                {/* Invoice description line (refund mode) */}
                {isRefundMode && (
                  <p className="text-[16px] text-[#4A5565] font-poppins mb-3">
                    {/* raised_on arrives as a raw ISO string and was printed as-is,
                        so the refund modal showed 2026-04-08T12:25:54.000Z while every
                        other date on screen was formatted (CVA-4542). */}
                    {formatDate(invoice.raised_on, 'DD MMMM YYYY')} - {invoice.template_name}
                  </p>
                )}

                {/* Payment Summary */}
                <div className="space-y-3">
                  {/* Invoice ID row (refund mode only) */}
                  {isRefundMode && (
                    <div className="flex justify-between items-center pb-1 border-b-[0.8px] border-[#D1D5DB]">
                      <span className="text-[16px] text-[#364153] font-poppins">
                        {tr('fe_modals.invoice_id', 'Invoice ID')}
                      </span>
                      <span className="text-[16px] font-semibold text-[#101828] font-poppins">
                        {invoice.invoice_id}
                      </span>
                    </div>
                  )}

                  <div className="flex justify-between items-center pb-1 border-b-[0.8px] border-[#D1D5DB]">
                    <span className="text-[16px] text-[#101828] font-poppins">
                      {tr('fe_modals.total_amount', 'Total Amount')}
                    </span>
                    <span className="text-[16px] font-semibold text-[#101828] font-poppins">
                      {currency} {totalAmount.toLocaleString()}
                    </span>
                  </div>

                  <div className="flex justify-between items-center pb-1 border-b-[0.8px] border-[#D1D5DB]">
                    <span className="text-[16px] text-[#101828] font-poppins">
                      {isRefundMode
                        ? tr('fe_modals.amount_paid', 'Amount Paid')
                        : tr('fe_modals.already_paid', 'Already Paid')}
                    </span>
                    <span className="text-[16px] font-semibold text-[#00A63E] font-poppins">
                      {currency} {alreadyPaid.toLocaleString()}
                    </span>
                  </div>

                  <div className="bg-[#EEE7FA] rounded-[6px] p-3 flex justify-between items-center">
                    <span className="text-[16px] font-semibold text-[#101828] font-poppins">
                      {isRefundMode
                        ? tr('fe_modals.refundable', 'Refundable')
                        : tr('fe_modals.balance_due', 'Balance Due')}
                    </span>
                    <span className="text-[20px] font-bold text-project-purple font-poppins">
                      {currency}{' '}
                      {isRefundMode
                        ? refundableAmount.toLocaleString()
                        : balanceDue.toLocaleString()}
                    </span>
                  </div>
                </div>
              </div>

              {/* Amount Input */}
              <AppInput
                label={
                  isRefundMode
                    ? tr('fe_modals.refund_amount', 'Refund Amount ({{currency}})').replace(
                        '{{currency}}',
                        currency,
                      )
                    : tr('fe_modals.amount_received', 'Amount Received ({{currency}})').replace(
                        '{{currency}}',
                        currency,
                      )
                }
                required
                value={amountReceived}
                onChange={(e) => {
                  setAmountReceived(e.target.value);
                  if (errors.amountReceived) {
                    setErrors({ ...errors, amountReceived: '' });
                  }
                }}
                placeholder={
                  isRefundMode
                    ? tr('fe_modals.enter_refund_amount', 'Enter refund amount')
                    : tr('fe_modals.enter_amount_received', 'Enter amount received')
                }
                type="number"
                error={errors.amountReceived}
                disabled={isRefundMode}
                readOnly={isRefundMode}
              />

              {/* Payment status alerts (payment mode only) */}
              {!isRefundMode && (
                <>
                  {/* Partial Payment Alert Box (Yellow) */}
                  {paymentStatus === PAYMENT_STATUS.PARTIAL && (
                    <div
                      className={`border-[1.6px] border-[#FFF085] rounded-[10px] px-[15.6px] py-[12.6px] flex gap-3 ${styles.partialAlertBg}`}
                    >
                      <div className="flex-1 flex flex-col gap-1">
                        <h4 className="text-[16px] font-semibold text-[#733E0A] font-poppins leading-6">
                          {tr('fe_modals.partial_payment', 'Partial Payment')}
                        </h4>
                        <p className="text-[13px] text-[#364153] font-poppins leading-5">
                          {tr('fe_modals.remaining_balance', 'Remaining balance:')} {currency}{' '}
                          {remainingBalance.toLocaleString()}
                        </p>
                        <div className="flex flex-col gap-1 mt-2">
                          <div className="flex justify-between items-center">
                            <span className="text-[12px] text-black font-poppins leading-4">
                              {tr('fe_modals.new_payment_progress', 'New Payment Progress')}
                            </span>
                            <span className="text-[12px] font-semibold text-black font-poppins leading-4">
                              {Math.round(paymentProgress)}%
                            </span>
                          </div>
                          <div
                            className={`h-2 rounded-full overflow-hidden ${styles.progressTrack}`}
                          >
                            {/* Intentional inline style: width is dynamic based on paymentProgress */}
                            <div
                              className={`h-full ${styles.progressFill}`}
                              style={{ width: `${paymentProgress}%` }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Exact Payment Alert Box (Green) */}
                  {paymentStatus === PAYMENT_STATUS.EXACT && (
                    <div
                      className={`border-[1.6px] border-[#B9F8CF] rounded-[10px] px-[15.6px] py-[12.6px] flex gap-3 ${styles.exactAlertBg}`}
                    >
                      <div className="flex-1 flex flex-col gap-1">
                        <h4 className="text-[16px] font-semibold text-[#0D542B] font-poppins leading-6">
                          {tr('fe_modals.exact_payment', 'Exact Payment')}
                        </h4>
                        <p className="text-[13px] text-[#364153] font-poppins leading-5">
                          {tr(
                            'fe_modals.invoice_marked_paid',
                            'Invoice will be marked as PAID with balance of {{currency}} 0.00',
                          ).replace('{{currency}}', currency)}
                        </p>
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* Date Input */}
              <AppCalenderInput
                label={
                  isRefundMode
                    ? tr('fe_modals.refund_date', 'Refund Date')
                    : tr('fe_modals.payment_date', 'Payment Date')
                }
                required
                value={paymentDate}
                onChange={(e) => {
                  setPaymentDate(e.value as Date);
                  if (errors.paymentDate) {
                    setErrors({ ...errors, paymentDate: '' });
                  }
                }}
                maxDate={isRefundMode ? new Date() : undefined}
                error={errors.paymentDate}
              />

              {/* Reference/Note Textarea */}
              <AppTextarea
                label={
                  isRefundMode
                    ? tr('fe_modals.reference_note_optional', 'Reference / Note (Optional)')
                    : tr('fe_modals.reference_note', 'Reference/Note')
                }
                value={referenceNote}
                onChange={(e) => setReferenceNote(e.target.value)}
                placeholder={
                  isRefundMode
                    ? tr(
                        'fe_modals.explain_refund_reason',
                        'Explain why this refund is being issued (e.g., Overpayment correction, Wrong payment amount, Parent request, etc.)',
                      )
                    : tr('fe_modals.optional_notes_or_reference', 'Optional notes or reference')
                }
                rows={3}
              />

              {/* Action Buttons */}
              <div className="flex gap-3 justify-end mt-2">
                <AppButton label={tr('fe_modals.cancel', 'Cancel')} outlined onClick={onCancel} />
                <AppButton
                  label={
                    isRefundMode
                      ? tr('fe_modals.process_refund', 'Process Refund')
                      : tr('fe_modals.record_payment', 'Record Payment')
                  }
                  onClick={handleRecordPayment}
                  disabled={!amountReceived || !paymentDate || isLoading}
                  loading={isLoading}
                />
              </div>
            </div>
          }
        />

        {/* Refund confirmation. Payment mode no longer confirms: over-balance amounts are
            rejected by validateForm, and an in-range amount submits directly. */}
        <ConfirmationModal
          ref={refundConfirmModalRef}
          title="Confirm Refund"
          icon="pi-question-circle"
          severity="primary"
          content={
            <p className="text-center">
              {tr(
                'fe_billing.confirm_refund_body',
                'Are you sure you want to refund {{amount}} for this invoice?',
                {
                  amount: `${currency} ${parseFloat(amountReceived || '0').toLocaleString()}`,
                },
              )}
            </p>
          }
          yesLabel="Yes, Confirm Refund"
          noLabel={tr('fe_modals.cancel', 'Cancel')}
          onYes={handleRefundConfirm}
          onNo={handleRefundCancel}
          yesDisabled={isLoading}
        />
      </>
    );
  },
);

CollectPaymentModal.displayName = 'CollectPaymentModal';

export default CollectPaymentModal;
